import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import PaginationControls from './PaginationControls';
import { computeAttendanceStats } from '../utils/attendanceStats';

const PAGE_SIZE = 20;

const TrainingAttendance = () => {
  const { id: trainingId } = useParams();
  const [training, setTraining] = useState(null); // State to hold training details
  const [attendees, setAttendees] = useState(null);
  //const [attendedUserIds, setAttendedUserIds] = useState(new Set()); // Track registered user IDs
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [results, setResults] = useState({});
  //const [selectedKIFlags, setSelectedKIFlags] = useState({});
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetch(`${API_BASE_URL}/trainings/get_training.php?id=${trainingId}`,{
        credentials: 'include'  // This ensures cookies (including session cookies) are sent
      })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        //if (data.success) {
          setTraining(data); // Set training details in state
        //}
      })
      .catch(error => console.error('Error fetching training:', error));
  }, [trainingId]);

  // Fetch attendance
  useEffect(() => {
    fetch(`${API_BASE_URL}/trainings/get_training_attendance.php?id=${trainingId}`,{
        credentials: 'include'  // This ensures cookies (including session cookies) are sent
      })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        // Handle both array and empty array responses
        //console.log('registed user data:', data);
        if (Array.isArray(data)) {
          setAttendees(data);
          console.log('attendees:', attendees);
          //const userIds = new Set(data.map(p => p.user_id));
          // console.log('userIds:', userIds);
          //setAttendedUserIds(userIds);
          // ✅ preload attendees
          setSelectedAttendees(data.filter(p => p.attended === 1).map(p => p.user_id));
          const resMap = {};
          data.forEach(p => {
            resMap[p.user_id] = {
              result_1: p.result_1 || '',
              result_2: p.result_2 || ''
            };
          });
          setResults(resMap);
          //console.log('attendedUserIds:', attendedUserIds);
           console.log('selectedAttendees:', selectedAttendees);
        } else {
          // Handle case where backend returns empty array
          setAttendees([]);
          //setAttendedUserIds(new Set());
          setSelectedAttendees([]);
          setResults({});

        }
      })
      .catch(error => console.error('Error fetching training:', error));
  }, [trainingId]);
 
 
  useEffect(() => {
    // Fetch participants under the admin's control
    fetch(`${API_BASE_URL}/trainings/get_training_participants.php?id=${trainingId}&detail=1`,{
        credentials: 'include'  // This ensures cookies (including session cookies) are sent
      })
      .then(response => response.json())
      .then(data => {
        setParticipants(Array.isArray(data) ? data : []);
        console.log('participants:', participants);

      })
      .catch(err => setError('Failed to fetch participants'));
  }, []);

  /*
  const handleCheckboxChange = (userId) => {
    setSelectedAttendees((prev) => {
      if (prev.includes(userId)) {
        const newSelected = prev.filter(id => id !== userId);
        //const newKIFlags = { ...selectedKIFlags };
        //delete newKIFlags[userId];
        //setSelectedKIFlags(newKIFlags);
        return newSelected;
      } else {
        return [...prev, userId];
      }
    });
  };
  */
   const handleCheckboxChange = (userId) => {
    setSelectedAttendees(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleResultChange = (userId, field, value) => {
    setResults(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value
      }
    }));
  };

  /*
  const handleKICheckboxChange = (userId) => {
    setSelectedKIFlags((prev) => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };
  */
  // Live — recomputes as checkboxes/results are edited, before Save is
  // clicked, from the same local state the table itself renders from.
  const stats = useMemo(() => {
    const rows = participants.map(p => ({
      attended: selectedAttendees.includes(p.user_id) ? 1 : 0,
      result_1: results[p.user_id]?.result_1,
      result_2: results[p.user_id]?.result_2,
    }));
    return computeAttendanceStats(rows);
  }, [participants, selectedAttendees, results]);

  const handleAttendance = () => {
    if (selectedAttendees.length === 0) {
      Swal.fire({ icon: 'warning', text: 'Please select at least one participant.' });
      return;
    }
    console.log('selectedAttendees:', selectedAttendees);

    const payload = {
      training_id: trainingId,
      attendees: participants.map(p => ({
        id: p.user_id,
        is_attend: selectedAttendees.includes(p.user_id) ? 1 : 0,
        result_1: results[p.user_id]?.result_1 || '',
        result_2: results[p.user_id]?.result_2 || ''
      }))
    };

    fetch(`${API_BASE_URL}/trainings/attended_training.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify(payload),
      credentials: 'include', // Include cookies for session
    })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        console.log("data",data);
        // Check if the response is JSON or plain text
        if (typeof data === 'string') {
          console.log('Non-JSON response:', data);
          Swal.fire({ icon: 'error', text: `Received non-JSON response: ${data}` });
        } else if (data.success) {
          //navigate('/users');
          Swal.fire({ icon: 'success', text: 'Participants attendance successfully updated' });
          // Update attended users
          /*
          const newUserIds = new Set(attendedUserIds);
          const newParticipants = [...attendees]; // clone to update

          selectedAttendees.forEach(id => {
            newUserIds.add(id);

            // If not already in attendees, add them
            if (!attendees.some(p => p.user_id === id)) {
              newParticipants.push({
                user_id: id
              });
            }
          });

          setAttendedUserIds(newUserIds);
          setAttendees(newParticipants);
          //setSelectedAttendees([]);
          setSelectedAttendees(newUserIds);
          //setSelectedKIFlags({});
          */
         setAttendees(
            participants.filter(p => selectedAttendees.includes(p.user_id))
          ); // ✅ sync attendees to selected ones
        } else {
          console.error('PHP Error:', data.error); // Log PHP error to the console
          Swal.fire({ icon: 'error', text: `Failed to update attendance: ${data.error}` });
        }
      })
      .catch(error => {
        console.error('Error updating attendance:', error);
        Swal.fire({ icon: 'error', text: 'Error: ' + error.message });
      });
  };

  return (
    <div className='p-2'>
      <h2>Update Training Attendance</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {/* Display training information */}
      {training && (
        <div>
          <h4>Training Details</h4>
          <p><strong>Code:</strong> {training.code}<br/>
          <strong>Title:</strong> {training.title}<br/>
          <strong>Description:</strong> {training.description}</p>
          <p><strong>Date:</strong> {training.start_date} to {training.end_date}<br/>
          <strong>Location:</strong> {training.location}</p>
        </div>
      )}
      <div className="mb-3">
        <div><strong>Attendance Rate:</strong> {stats.attendanceRate !== null ? `${stats.attendanceRate.toFixed(1)}% (${stats.attendedCount}/${stats.total})` : '-'}</div>
        <div><strong>Average Result 1:</strong> {stats.avgResult1 !== null ? stats.avgResult1.toFixed(1) : 'N/A (no numeric entries)'}</div>
        <div><strong>Average Result 2:</strong> {stats.avgResult2 !== null ? stats.avgResult2.toFixed(1) : 'N/A (no numeric entries)'}</div>
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Attended</th>
            <th>Result 1</th>
            <th>Result 2</th>
            <th>Designation</th>
            <th>Company</th>
          </tr>
        </thead>
        <tbody>
          {participants.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((employee, index) => {
            //const isAttended = attendedUserIds.has(employee.user_id);
            const isChecked = selectedAttendees.includes(employee.user_id);
            return (
            <tr key={employee.id}>
              <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
              <td>{employee.name}</td>
              <td className="pl-4 text-center">
                <Form.Check
                type="checkbox"
                checked={isChecked}
                onChange={() => handleCheckboxChange(employee.user_id)}
                />
              </td>
              
              <td className="text-center">
                  <Form.Control
                    size="sm"
                    type="text"
                    value={results[employee.user_id]?.result_1 || ''}
                    onChange={(e) => handleResultChange(employee.user_id, 'result_1', e.target.value)}
                  />
                </td>
                <td className="text-center">
                  <Form.Control
                    size="sm"
                    type="text"
                    value={results[employee.user_id]?.result_2 || ''}
                    onChange={(e) => handleResultChange(employee.user_id, 'result_2', e.target.value)}
                  />
                </td>  
              <td>{employee.designation}</td>
              <td>{employee.company}</td>
            </tr>
          );
        })}
        </tbody>
      </Table>
      <PaginationControls page={page} totalPages={Math.max(1, Math.ceil(participants.length / PAGE_SIZE))} onPageChange={setPage} />
      <Button variant="primary" className="mt-3" onClick={handleAttendance}>Save Attendance</Button>
    </div>
  );
};

export default TrainingAttendance;
